import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';

import { proxyAxios } from 'api/apiBase/axios';
import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { cexExchangesTimeframes, IChartTimeframe, KLine } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';
import { ECexName } from 'web3';

import { BaseStrategy, IInitArgs } from '../base_strategy';

export class BitmartStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  depthWs: ReconnectingWebSocket | undefined;
  pingInterval: ReturnType<typeof setInterval> | undefined;
  depth: number;
  asks: string[][];
  bids: string[][];
  pair: string;
  precision: number;
  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  symbolId: number;
  depthConfig: { precision: number; symbolId: number } | undefined;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}_${quote.toUpperCase()}`;
    this.precision = 1;
    this.symbolId = 1;
    this.asks = [];
    this.bids = [];
    this.depth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  setOrderbookDepth(depth: number) {
    // unsubscribe from current depth before changing it
    if (this.ws) {
      this.ws.send(
        JSON.stringify({ op: 'unsubscribe', args: [`spot/depth${this.depth}:${this.pair}`] }),
      );
    }

    this.depth = depth;

    if (this.ws) {
      this.ws.send(
        JSON.stringify({ op: 'subscribe', args: [`spot/depth${this.depth}:${this.pair}`] }),
      );
    }
  }

  fetchSymbolId = async () => {
    try {
      const res = await axios.get<{ data: { symbols: { symbol_id: number; symbol: string }[] } }>(
        `https://api-cloud.bitmart.com/spot/v1/symbols/details`,
      );

      const find = res.data.data.symbols.find(el => el.symbol === this.pair);

      if (find) {
        this.symbolId = find.symbol_id;
      }
    } catch (error) {
      console.log('bitmart symbol error: ', error);
    }
  };

  fetchPrecision = async () => {
    try {
      const url = new URL(
        `https://api-cloud.bitmart.com/spot/quotation/v3/books?symbol=${this.pair}&limit=20`,
      );

      const res = await proxyAxios.get<{
        data: { asks: [string, string][]; bids: [string, string][] };
      }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      let prec = 1;

      if (res.data) {
        try {
          prec = res.data.data.asks[0][0].split('.')[1].length;
        } catch (error) {}
      }

      this.precision = prec;
      this.precisionUpdate(prec);
    } catch (error) {
      console.log('Bitmart precision error: ', error);
    }
  };

  fetchKlines = async ({
    base,
    quote,
    limit,
    from,
    to,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }) => {
    const innerInterval = cexExchangesTimeframes[interval][ECexName.bitmart];

    try {
      const result = await axios.get<{ data: string[][] }>(
        'https://api-cloud.bitmart.com/spot/quotation/v3/klines',
        {
          params: {
            symbol: `${base.toUpperCase()}_${quote.toUpperCase()}`,
            limit,
            step: innerInterval,
            after: Math.floor(from / 1000),
            before: Math.floor(to / 1000),
          },
        },
      );

      return (result.data.data ?? []).map(el => [
        Number(el[0]) * 1000,
        el[1],
        el[2],
        el[3],
        el[4],
        el[5],
      ]) as KLine[];
    } catch (error) {
      console.log('Bitmart klines error: ', error);
      return [];
    }
  };

  fetchTrades = async ({ base, quote, limit }: { base: string; quote: string; limit: number }) => {
    try {
      const url = new URL('https://api-cloud.bitmart.com/spot/quotation/v3/trades');
      const params = new URLSearchParams({
        symbol: `${base.toUpperCase()}_${quote.toUpperCase()}`,
        limit: limit.toString(),
      });

      url.search = params.toString();

      const result = await proxyAxios.get<{ data: string[][] }>('/api/v1/proxy', {
        params: {
          url: url.toString(),
        },
      });

      return (
        (result.data.data ?? []).map(item => ({
          price: item[2],
          quantity: item[3],
          timestamp: item[1],
          side: item[4],
        })) ?? []
      );
    } catch (error) {
      console.log('Bitmart trades error: ', error);
      return [];
    }
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    if (this.ws) {
      this.closeWebSocket();
    }

    const ws = new ReconnectingWebSocket(
      'wss://ws-manager-compress.bitmart.com/api?protocol=1.1',
      [],
      {
        maxRetries: 10,
        minReconnectionDelay: 1000,
      },
    );

    this.ws = ws;

    const intervalChannel = {
      60000: 'kline1m',
      300000: 'kline5m',
      900000: 'kline15m',
      3600000: 'kline1H',
      14400000: 'kline4H',
      86400000: 'kline1D',
      604800000: 'kline1W',
    }[interval];

    this.pingInterval = setInterval(() => {
      if (this.ws) {
        this.ws.send('ping');
      }
    }, 19 * 1000);

    this.ws.onopen = () => {
      ws.send(JSON.stringify({ op: 'subscribe', args: [`spot/depth${this.depth}:${this.pair}`] }));
      ws.send(JSON.stringify({ op: 'subscribe', args: [`spot/ticker:${this.pair}`] }));
      ws.send(JSON.stringify({ op: 'subscribe', args: [`spot/${intervalChannel}:${this.pair}`] }));
      ws.send(JSON.stringify({ op: 'subscribe', args: [`spot/trade:${this.pair}`] }));
    };

    this.ws.onmessage = event => {
      try {
        // Skip pong messages
        if (event.data === 'pong') return;

        const data = JSON.parse(event.data);

        if (data.table === 'spot/ticker' && data.data) {
          const { fluctuation, base_volume_24h, quote_volume_24h, last_price, high_24h, low_24h } =
            data.data[0];

          this.onTickerUpdate({
            baseVolume: base_volume_24h,
            change: normalizeNumber(Number(fluctuation) * 100),
            close: last_price,
            high: high_24h,
            last: last_price,
            low: low_24h,
            quoteVolume: quote_volume_24h,
          });
        }

        if (data.table === `spot/${intervalChannel}` && data.data) {
          const [time, open, high, low, close, volume] = data.data[0].candle;

          this.klineUpdate([time, open, high, low, close, volume]);
        }

        if (data.table === 'spot/trade' && data.data) {
          const { ms_t, price, side, size } = data.data[0];

          this.onTradeUpdate({
            price,
            quantity: size,
            timestamp: ms_t,
            side,
          });
        }

        if (data.table && data.table.includes('spot/depth') && data.data) {
          try {
            const { asks, bids } = data.data[0] as {
              asks: any[];
              bids: any[];
            };

            this.asks = asks.map(el => [el.price, el.amount]);
            this.bids = bids.map(el => [el.price, el.amount]);

            this.onOrderbookUpdate({ bids, asks });
          } catch (error) {
            console.log('Bitmart depth error: ', error);
          }
        }
      } catch (error) {
        console.log('Bitmart socket error', error);
      }
    };
  };

  closeWebSocket = () => {
    if (this.ws) {
      this.ws.close();
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
  };

  // Actually precision don't support by new Bitmart WS API for depth
  // https://developer-pro.bitmart.com/en/spot/#public-depth-all-channel
  switchOrderbookTick = ({ tick }: { tick: number }) => {
    if (this.ws && this.depthConfig) {
      this.ws.send(
        JSON.stringify({ op: 'unsubscribe', args: [`spot/depth${this.depth}:${this.pair}`] }),
      );

      this.depthConfig = {
        precision: tick,
        symbolId: this.symbolId,
      };

      this.ws.send(
        JSON.stringify({ op: 'subscribe', args: [`spot/depth${this.depth}:${this.pair}`] }),
      );
    }
  };

  getOrderbookTicks = (prec: number) =>
    Array.from({ length: prec })
      .fill(0)
      .map((_, idx) => idx + 1)
      .map(el => ({ value: normalizeNumber(1 / 10 ** el), tick: el, id: el }))
      .slice(-3);

  fetch = async () => {
    await Promise.all([this.fetchSymbolId(), this.fetchPrecision()]);
  };

  static init = async (args: IInitArgs) => {
    const instance = new BitmartStrategy(args);

    await instance.fetch();

    return instance;
  };
}
